import { useMemo } from "react";
import { GetSubscriptionPlansResponse } from "@/redux/apis/subscription/types";
import { useSubscriptionPlans } from "./useSubscriptionPlans";

interface PlanLimits {
  maxVehicles: number;
  maxDrivers: number;
}

const UNLIMITED = 100000;
const FALLBACK_LIMITS: Record<string, PlanLimits> = {
  solo: { maxVehicles: 1, maxDrivers: 1 },
  fleet: { maxVehicles: 10, maxDrivers: 20 },
  enterprise: { maxVehicles: UNLIMITED, maxDrivers: UNLIMITED },
};

interface PlanLimitsMap {
  byId: Record<string, PlanLimits>;
  bySlug: Record<string, PlanLimits>;
}

/**
 * Extract plan limits from the subscription plans API response.
 * Looks for features with slugs "max-driver-count" and "max-vehicle-count"
 * and reads the defaultValue for each plan.
 * Returns maps keyed by both subscriptionId and slug for flexible lookups.
 */
function extractPlanLimits(data: GetSubscriptionPlansResponse): PlanLimitsMap {
  const byId: Record<string, PlanLimits> = {};
  const bySlug: Record<string, PlanLimits> = {};

  const allFeatures = data.featureGroups.flatMap((group) => group.features);
  const driverFeature = allFeatures.find((f) => f.slug === "max-driver-count");
  const vehicleFeature = allFeatures.find((f) => f.slug === "max-vehicle-count");

  for (const sub of data.subscriptions) {
    const driverSub = driverFeature?.subscriptions.find((s) => s.subscriptionId === sub.subscriptionId);
    const vehicleSub = vehicleFeature?.subscriptions.find((s) => s.subscriptionId === sub.subscriptionId);

    const driverValue = driverSub?.defaultValue;
    const vehicleValue = vehicleSub?.defaultValue;

    const limits: PlanLimits = {
      maxDrivers: driverValue && driverValue.toLowerCase() !== "unlimited" ? Number(driverValue) : UNLIMITED,
      maxVehicles: vehicleValue && vehicleValue.toLowerCase() !== "unlimited" ? Number(vehicleValue) : UNLIMITED,
    };

    byId[sub.subscriptionId] = limits;
    bySlug[sub.slug.toLowerCase()] = limits;
  }

  return { byId, bySlug };
}

/**
 * Hook to get subscription plan limits from the API.
 * Returns a getPlanLimits function and a shouldShowLimitIndicator function
 * that use real API data instead of hardcoded values.
 */
export const useSubscriptionPlanLimits = () => {
  // Group-aware: a group provider's limits come from their group's plans
  const { data } = useSubscriptionPlans();

  const planLimitsMap = useMemo(() => {
    if (!data) return { byId: {}, bySlug: FALLBACK_LIMITS };
    return extractPlanLimits(data);
  }, [data]);

  const getPlanLimits = (planSlug: string): PlanLimits => {
    const slug = planSlug.toLowerCase();
    return planLimitsMap.bySlug[slug] ?? FALLBACK_LIMITS.solo;
  };

  const getPlanLimitsById = (subscriptionId: string): PlanLimits => {
    return planLimitsMap.byId[subscriptionId] ?? FALLBACK_LIMITS.enterprise;
  };

  const shouldShowLimitIndicator = (planSlug: string): boolean => {
    const limits = getPlanLimits(planSlug);
    return limits.maxVehicles < UNLIMITED || limits.maxDrivers < UNLIMITED;
  };

  const shouldShowLimitIndicatorById = (subscriptionId: string): boolean => {
    const limits = getPlanLimitsById(subscriptionId);
    return limits.maxVehicles < UNLIMITED || limits.maxDrivers < UNLIMITED;
  };

  return { getPlanLimits, getPlanLimitsById, shouldShowLimitIndicator, shouldShowLimitIndicatorById };
};
